import { useEffect, useRef } from "react";
import { toast } from "react-toastify";

import { readEmbeddedBuildMeta, type DeployBuildMeta } from "../lib/deployBuildMeta";

const POLL_MS = 3 * 60 * 1000;

async function fetchRemoteVersion(): Promise<string | null> {
  try {
    const response = await fetch(
      `/deploy-version.json?t=${Date.now()}`,
      { cache: "no-store" }
    );
    if (!response.ok) return null;
    const data = (await response.json()) as DeployBuildMeta;
    return data.version ?? null;
  } catch {
    return null;
  }
}

/**
 * Compara a versão embutida no HTML (ou deploy-version.json) com o remoto.
 * Se mudou (novo deploy), avisa e recarrega — evita ficar preso em JS antigo em cache.
 */
export function useDeployVersionCheck(): void {
  const localVersionRef = useRef<string | null>(null);
  const promptedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;

    const resolveLocal = async (): Promise<string | null> => {
      const embedded = readEmbeddedBuildMeta()?.version ?? null;
      if (embedded) return embedded;
      return fetchRemoteVersion();
    };

    const check = async () => {
      if (promptedRef.current) return;
      if (!localVersionRef.current) return;

      const remoteVersion = await fetchRemoteVersion();
      if (!remoteVersion || remoteVersion === localVersionRef.current) return;

      promptedRef.current = true;
      toast.info(
        `Nova versão (${remoteVersion}) disponível. A página será atualizada.`,
        { autoClose: 2500 }
      );
      window.setTimeout(() => {
        window.location.reload();
      }, 2500);
    };

    void (async () => {
      const local = await resolveLocal();
      if (cancelled) return;
      localVersionRef.current = local;
      if (!local) return;
      await check();
    })();

    const onVisible = () => {
      if (document.visibilityState === "visible") {
        void check();
      }
    };

    const intervalId = window.setInterval(() => {
      void check();
    }, POLL_MS);

    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, []);
}
